/**
 * The control row the prose pages put a curve's constants in.
 *
 * One component for both pages, because they are the same thing seen twice: a
 * curve, and the two or three numbers that bend it. The evolution page bends
 * `E = (M/S)^p`, the threat page bends `P = a·E^n + b·E`, and a reader moving
 * between them should not have to learn a second control.
 *
 * Sliders rather than the charts page's number fields. These rows sit directly
 * above the plot they move, and the question being asked here is "what does
 * this constant *do*" — which is a drag, not a typed value. The charts page
 * keeps its fields for the other question, "is it 25 or 30".
 *
 * Layout is flex rather than a fixed grid so the same row holds two sliders or
 * three, with the action slot keeping its place at the end either way.
 */

import type { ReactNode } from 'react';

export function ParamRow({ children }: { children: ReactNode }) {
  return <div className="param-row">{children}</div>;
}

/**
 * The last slot of the row: presets, resets, and any switch that is not a
 * number. Always present, so the sliders start in the same place on every
 * branch even when there is nothing to put in it.
 */
export function ParamActions({ children }: { children: ReactNode }) {
  return <div className="param-actions">{children}</div>;
}

export function ParamSlider({
  label,
  hint,
  value,
  min,
  max,
  step,
  format,
  note,
  onChange,
}: {
  label: string;
  /** What the constant is, in the formula the row belongs to. */
  hint: string;
  value: number;
  min: number;
  max: number;
  step: number;
  /** The value as the formula writes it, e.g. `p = 0.30`. */
  format: (value: number) => string;
  /** What the value means in plain terms, under the slider. */
  note: string;
  onChange: (value: number) => void;
}) {
  return (
    <div className="param">
      <span className="param-head">
        <b>{label}</b>
        <span className="param-value">{format(value)}</span>
      </span>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <span className="param-note">
        {hint} · <b>{note}</b>
      </span>
    </div>
  );
}
